import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug);
  const [filteredComics, setFilteredComics] = useState(comics);

  useEffect(() => {
    setActiveSlug(slug);
    if (slug === 'all') {
      setFilteredComics(comics);
    } else {
      setFilteredComics(comics.filter((c) => c.category === slug));
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory
      ? `${currentCategory.name}漫画 - 韩漫画网`
      : '全部分类 - 韩漫画网',
    description: currentCategory
      ? `韩漫画网${currentCategory.name}漫画分类，免费在线观看韩国${currentCategory.name}漫画。${currentCategory.description}`
      : '韩漫画网全部分类，免费在线观看韩国漫画。涵盖恋爱、奇幻、动作、悬疑等多种类型。',
    url: `https://hanmanhua.com/category/${activeSlug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫画网',
      url: 'https://hanmanhua.com',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="py-6 md:py-8 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory ? currentCategory.name : '全部分类' },
              ]}
            />

            <div className="mt-6">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900">
                {currentCategory ? `${currentCategory.name}漫画` : '全部分类'}
              </h1>
              <p className="mt-2 text-sm md:text-base text-foreground-500">
                {currentCategory
                  ? currentCategory.description
                  : '浏览韩漫画网全部漫画资源'}
              </p>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md py-3 px-4 md:px-6">
          <div className="max-w-7xl mx-auto overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-2 min-w-max px-1 py-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Comic List */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {filteredComics.length > 0 ? (
              <>
                <div className="flex items-center justify-between mb-8">
                  <p className="text-sm text-foreground-500">
                    共找到 <strong className="text-foreground-800">{filteredComics.length}</strong> 部漫画
                  </p>
                  <div className="flex items-center gap-2 text-sm text-foreground-400">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-sort-desc"></i> 按热度排序
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {filteredComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
                </div>
                <h3 className="mt-6 text-lg font-semibold text-foreground-700">
                  暂无漫画
                </h3>
                <p className="mt-2 text-sm text-foreground-400">
                  该分类下暂时还没有漫画，请浏览其他分类
                </p>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 mt-6 px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  查看全部漫画
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}